import { useMemo } from "react";
import type { ModifierState } from "@/domain/profile-resolver/index";
import type { Keyboard as KeyboardModel, RenderKeycap } from "@/domain/types";
import { rotatedBounds, splitPlatePad } from "@/hardware/geometry";
import { modifierFamily } from "@/hardware/modifier-family";
import type { CapStyle } from "@/render/tweaks/use-tweaks";
import { isDimmed } from "./cap-face";
import { Keycap } from "./Keycap";

interface KeyboardProps {
  board: KeyboardModel;
  keycaps: RenderKeycap[];
  /** The shared key unit, owned by the page (useSurfaceUnit) so the mouse map scales with the board. */
  unit: number;
  showNames?: boolean;
  keycapStyle?: CapStyle;
  /** Keycap art: names (default) or opt-in icon tiles. */
  keycapLabel?: "names" | "icons";
  /** Resolves a command id to its icon-tile SVG (passed straight through to each Keycap). */
  iconFor?: (cmd: string | undefined) => string | null;
  /** Backlight strength 0-1.5; scales the backlit outer halo. */
  keyGlow?: number;
  /** Active modifier families; matching modifier caps get the active outline. */
  mods?: ModifierState;
  /** Category ids kept lit while isolating; bound caps outside them dim. */
  dimCategories?: string[] | null;
  /** The search/hover flashlight: when set, every cap whose code is NOT listed dims. */
  litCodes?: string[] | null;
  /** While a non-empty combo is held, dim base-fallback caps so the combo's own read. */
  dimFallback?: boolean;
  /** Codes whose caps are pressed (depressed) - the press-feedback transient. */
  pressedCodes?: string[] | null;
  onPress?: (code: string, legend: string) => void;
}

export function Keyboard({
  board,
  keycaps,
  unit,
  showNames = true,
  keycapStyle = "backlit",
  keycapLabel = "names",
  iconFor,
  keyGlow = 0.85,
  mods = {},
  dimCategories = null,
  litCodes = null,
  dimFallback = false,
  pressedCodes = null,
  onPress,
}: KeyboardProps) {
  // Unit-independent plate bounds per region; the trig only re-runs when the keys change.
  const regionBounds = useMemo(() => {
    const regions: Record<string, RenderKeycap[]> = {};
    for (const k of keycaps) {
      const rid = k.region ?? "main";
      const list = regions[rid] ?? [];
      list.push(k);
      regions[rid] = list;
    }
    return Object.entries(regions).map(([rid, ks]) => ({ rid, ...rotatedBounds(ks) }));
  }, [keycaps]);

  const pad = Math.max(8, unit * 0.3);

  // Split plates (P1): with two side-by-side regions the facing edges share the gap between the
  // halves. A full pad on each would collide when that gap is narrow (the QMK/ErgoDox thumb clusters
  // angle inward, leaving as little as 0.3u between the halves' bounding boxes). Pad each facing edge
  // by less than half the gap so the two plates keep a clean channel instead of overlapping. ReCore /
  // Moonlander have a wide gap and are unaffected (innerPad stays the full pad).
  const split = (() => {
    if (regionBounds.length !== 2) return null;
    const [a, b] = [...regionBounds].sort((p, q) => p.minX - q.minX);
    if (!a || !b) return null;
    const innerPad = splitPlatePad(b.minX - a.maxX, unit, pad);
    return { leftRid: a.rid, rightRid: b.rid, innerPad };
  })();

  return (
    <div className="kb-deck" style={{ width: board.width * unit, height: board.height * unit }}>
      {regionBounds.map((b) => {
        const padLeft = split && b.rid === split.rightRid ? split.innerPad : pad;
        const padRight = split && b.rid === split.leftRid ? split.innerPad : pad;
        return (
          <div
            key={b.rid}
            className="kb-plate"
            style={{
              left: b.minX * unit - padLeft,
              top: b.minY * unit - pad,
              width: (b.maxX - b.minX) * unit + padLeft + padRight,
              height: (b.maxY - b.minY) * unit + 2 * pad,
              borderRadius: Math.max(10, unit * 0.34),
            }}
          />
        );
      })}
      {keycaps.map((cap) => {
        // a dead cap (null active code) is never a modifier, never pressed, never lit
        const family = cap.code != null ? modifierFamily(cap.code) : null;
        // press/flashlight key on the cap's active code; a code shared by two physical
        // keys lights both, the uniform "every key that fires this shows it" rule
        const pressed = cap.code != null && (pressedCodes?.includes(cap.code) ?? false);
        // a pressed cap is always full-strength (its depress is the focus); otherwise the shared
        // dim rule (cap-face.isDimmed) decides, so the board and the mouse never disagree.
        const dimmed = !pressed && isDimmed(cap, { dimCategories, litCodes, dimFallback });
        return (
          <Keycap
            key={`${cap.code}@${cap.x},${cap.y}`}
            cap={cap}
            unit={unit}
            showNames={showNames}
            keycapStyle={keycapStyle}
            keycapLabel={keycapLabel}
            iconFor={iconFor}
            keyGlow={keyGlow}
            activeMod={family !== null && mods[family] === true}
            dim={dimmed}
            pressed={pressed}
            onPress={onPress}
          />
        );
      })}
    </div>
  );
}
